/* eslint-disable @typescript-eslint/no-explicit-any */
import { ipv4TokenRegExp, isIpv4 } from "@shared/utils/ipv4Token";

export type Accessor<T> = (row: T) => unknown;

export interface FilterField<T> {
  /** Human label shown to users */
  label: string;
  /** Internal key id (lowercase, no spaces);*/
  key?: string;
  /** How to pull a value from the row */
  accessor: Accessor<T>;
  /** Optional: mark this field searchable in free-text mode */
  inFreeText?: boolean;
  /** Optional: tell the engine this field is numeric or date */
  type?: "string" | "number" | "date" | "boolean";
  parseRhs?: (raw: string) => unknown;
}

export interface ParsedFilter {
  key: string;   // may be empty for free text
  value: string; // raw user input (right side)
  op?: ":" | "=" | ">" | "<" | ">=" | "<="; // optional operator
}

export function labelId(label: string): string {
  return label.replace(/\s+/g, "").toLowerCase(); // "Port ID" -> "portid"
}

function localDayKey(d: Date): number {
  // yyyymmdd numeric key in LOCAL time
  return d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
}

function formatDDMMYYYYLocal(d: Date): string {
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

export function coerce(value: unknown, type?: FilterField<any>["type"]) {
  if (value == null) return null;

  switch (type) {
    case "number": {
      const n = typeof value === "number" ? value : Number(String(value).replace(/,/g, ""));
      return Number.isFinite(n) ? n : null;
    }
    case "date": {
      const d = value instanceof Date ? value : new Date(String(value));
      return isNaN(d.getTime()) ? null : d;
    }
    case "boolean": {
      if (typeof value === "boolean") return value;
      const s = String(value).toLowerCase();
      if (["true", "1", "yes"].includes(s)) return true;
      if (["false", "0", "no"].includes(s)) return false;
      return null;
    }
    default:
      return value == null ? "" : String(value);
  }
}

// export function parseToken(input: string): ParsedFilter {
//   // Support key:value, key=value, key>=..., key<=..., key>..., key<...
//   const m = input.match(/^([\w\s-]+)\s*(>=|<=|=|:|>|<)\s*(.+)$/);
//   if (m) return { key: m[1].trim(), op: m[2] as any, value: m[3].trim() };
//   // otherwise free text
//   return { key: "", value: input.trim() };
// }

export function parseToken(input: string): ParsedFilter {
  // allow any characters in the key, up to the operator
  const m = input.match(/^\s*(.+?)\s*(>=|<=|=|:|>|<)\s*(.+?)\s*$/);
  if (m) return { key: m[1].trim(), op: m[2] as any, value: m[3].trim() };
  return { key: "", value: input.trim() };
}

function normalizeKey(k: string) {
  return k.replace(/\s+/g, "").toLowerCase();
}

/**
 * Builds a predicate from filters + a field config.
 */
export function buildPredicate<T>(
  filters: ParsedFilter[],
  fields: FilterField<T>[],
  opts?: {
    freeTextFallback?: FilterField<T>[];
    /**
     * Free text that is an IPv4 matches only as a whole address (`.25` does not
     * find `.254`). Off by default: free text is a substring, as it always was.
     */
    ipv4WholeToken?: boolean;
  }
): (row: T) => boolean {
  // Build maps for lookups by label or key id
  const byId = new Map<string, FilterField<T>>();
  const byLabel = new Map<string, FilterField<T>>();
  for (const f of fields) {
    const id = f.key ?? labelId(f.label);
    byId.set(normalizeKey(id), f);
    byLabel.set(normalizeKey(f.label), f);
  }

  // Decide which fields are scanned on free text
  const freeTextFields =
    opts?.freeTextFallback ??
    fields.filter((f) => f.inFreeText !== false); // default: all that don't opt-out

  return (row: T) => {
    if (!filters.length) return true;

    // All filters must match (AND)
    return filters.every((flt) => {
      if (!flt.value) return true;

      // FREE TEXT
      if (!flt.key) {
        const needle = flt.value.toLowerCase();
        // Opt-in (`ipv4WholeToken`): an IPv4 matches as a whole address.
        const ipToken = opts?.ipv4WholeToken && isIpv4(needle) ? ipv4TokenRegExp(needle.trim()) : null;
        return freeTextFields.some((f) => {
          const v = coerce(f.accessor(row), f.type);
          if (v == null) return false;
          const text = String(v).toLowerCase();
          return ipToken ? ipToken.test(text) : text.includes(needle);
        });
      }

      // KEYED FILTER
      const kNorm = normalizeKey(flt.key);
      const field =
        byId.get(kNorm) ||
        byLabel.get(kNorm); // accept either "Port ID" or "portid"

      if (!field) return false;

      const rowValue = coerce(field.accessor(row), field.type);
      const op = flt.op ?? ":"; // default to substring for strings, equals for non-strings
      const rhsRaw = flt.value;

      // Choose comparison by field type
      if (field.type === "number") {
        const lhs = typeof rowValue === "number" ? rowValue : null;
        const parsed = field.parseRhs ? field.parseRhs(rhsRaw) : Number(rhsRaw.replace(/,/g, ""));
        const rhs = typeof parsed === "number" ? parsed : NaN;
        if (lhs == null || !Number.isFinite(rhs)) return false;
        switch (op) {
          case "=": return lhs === rhs;
          case ">": return lhs > rhs;
          case "<": return lhs < rhs;
          case ">=": return lhs >= rhs;
          case "<=": return lhs <= rhs;
          case ":": // treat ":" as "contains" → compare as string
            return String(lhs).includes(String(rhs));
          default: return false;
        }
      }

      if (field.type === "date") {
        const lhs = rowValue instanceof Date ? rowValue : null;
        const rhsParsed = field.parseRhs ? field.parseRhs(rhsRaw) : new Date(rhsRaw);
        const rhsDate = rhsParsed instanceof Date ? rhsParsed : new Date(NaN);
        if (!lhs) return false;
      
        // Use local-day semantics for exact/inequalities
        const lhsKey = localDayKey(lhs);
      
        if (op === ":") {
          // contains → compare against the formatted label the user sees
          const label = formatDDMMYYYYLocal(lhs);
          return label.toLowerCase().includes(rhsRaw.trim().toLowerCase());
        }
      
        if (isNaN(rhsDate.getTime())) return false;
        const rhsKey = localDayKey(rhsDate);
      
        switch (op) {
          case "=":  return lhsKey === rhsKey;
          case ">":  return lhsKey >  rhsKey;
          case "<":  return lhsKey <  rhsKey;
          case ">=": return lhsKey >= rhsKey;
          case "<=": return lhsKey <= rhsKey;
          default:   return false;
        }
      }      

      if (field.type === "boolean") {
        const lhs = typeof rowValue === "boolean" ? rowValue : null;
        const rhs = ["true", "1", "yes"].includes(rhsRaw.toLowerCase());
        if (lhs == null) return false;
        return op === ":" || op === "=" ? lhs === rhs : false;
      }
      const rawLhs = String(rowValue ?? "");
      const rawRhs = rhsRaw;
    
      // string default
      const lhsNorm = field.parseRhs ? String(field.parseRhs(rawLhs) ?? "") : rawLhs.toLowerCase();
      const rhsNorm = field.parseRhs ? String(field.parseRhs(rawRhs) ?? "") : rawRhs.toLowerCase();
    
      if (!field.parseRhs) {
        // existing behavior (case-insensitive)
        const lhs = rawLhs.toLowerCase();
        const rhs = rawRhs.toLowerCase();
        if (op === "=") return lhs === rhs;
        return lhs.includes(rhs); // ":" or default → contains
      }

      if (op === "=") return lhsNorm === rhsNorm;
      return lhsNorm.includes(rhsNorm);
    });
  };
}
